import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Button, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField, Snackbar, Alert, CircularProgress, SelectChangeEvent, Autocomplete, Grid
} from '@mui/material';
import TitleBar from '../../components/TitleBar/TitleBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { 
  getAllReviewEntityTypes, 
  deleteReviewEntityType, 
  updateReviewEntityType, 
  createReviewEntityType,
  getAllReviewDimensions,
  ReviewEntityType,
  ReviewDimension 
} from '../../services/reviewEntityTypeService';
import { getAllMetricLevels, MetricLevel } from '../../services/metricLevelService';

const MAX_METRIC_LEVELS = 6;

const EntityTypeSetup: React.FC = () => {
  const navigate = useNavigate();
  const [entityTypes, setEntityTypes] = useState<ReviewEntityType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<string>('');

  const [openViewDialog, setOpenViewDialog] = useState<boolean>(false);
  const [viewEntityType, setViewEntityType] = useState<ReviewEntityType | null>(null);

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'edit' | 'add'>('edit');
  const [currentEntityType, setCurrentEntityType] = useState<ReviewEntityType>({
    name: '',
    display_label: '',
    category: '',
    description: '',
    review_dimension_names: [],
    metric_level_names: []
  });
  const [originalName, setOriginalName] = useState<string>('');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const [categories, setCategories] = useState<string[]>([]);
  const [reviewDimensions, setReviewDimensions] = useState<ReviewDimension[]>([]);
  const [metricLevels, setMetricLevels] = useState<MetricLevel[]>([]);

  useEffect(() => {
    fetchEntityTypes();
    fetchReviewDimensions();
    fetchMetricLevels();
  }, []);

  const fetchEntityTypes = async () => {
    setLoading(true);
    try {
      const data = await getAllReviewEntityTypes();
      const sortedData = [...data].sort((a, b) => a.category.localeCompare(b.category));
      setEntityTypes(sortedData);
      const uniqueCategories = Array.from(new Set(sortedData.map(item => item.category.toUpperCase())));
      setCategories(uniqueCategories);
    } catch (err) {
      setError('Failed to fetch entity types');
      console.error('Error fetching entity types:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewDimensions = async () => {
    try {
      const data = await getAllReviewDimensions();
      setReviewDimensions(data);
    } catch (err) {
      console.error('Error fetching review dimensions:', err);
    }
  };

  const fetchMetricLevels = async () => {
    try {
      const data = await getAllMetricLevels();
      setMetricLevels(data);
    } catch (err) {
      console.error('Error fetching metric levels:', err);
    }
  };

  const handleOpenDeleteDialog = (name: string) => {
    setDeleteTarget(name);
    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget('');
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteReviewEntityType(deleteTarget);
      setSnackbar({
        open: true,
        message: `Entity type "${deleteTarget}" deleted successfully`,
        severity: 'success'
      });
      fetchEntityTypes();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Unknown error occurred';
      setSnackbar({
        open: true,
        message: `Failed to delete entity type: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleOpenViewDialog = (entityType: ReviewEntityType) => {
    setViewEntityType(entityType);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewEntityType(null);
  };

  const handleOpenEditDialog = (entityType: ReviewEntityType) => {
    setEditMode('edit');
    setCurrentEntityType({
      ...entityType,
      category: entityType.category?.toUpperCase() || '',
      review_dimension_names: entityType.associated_review_dimension_names || [],
      metric_level_names: entityType.metric_level_names || []
    });
    setOriginalName(entityType.name);
    setOpenEditDialog(true);
  };

  const handleOpenAddDialog = () => {
    setEditMode('add');
    setCurrentEntityType({
      name: '',
      display_label: '',
      category: '',
      description: '',
      review_dimension_names: [],
      metric_level_names: []
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    setCurrentEntityType(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleSubmitEntityType = async () => {
    try {
      if (editMode === 'edit') {
        await updateReviewEntityType(originalName, currentEntityType);
        setSnackbar({
          open: true,
          message: `Entity type "${currentEntityType.display_label}" updated successfully`,
          severity: 'success'
        });
      } else {
        await createReviewEntityType(currentEntityType);
        setSnackbar({
          open: true,
          message: `Entity type "${currentEntityType.display_label}" created successfully`,
          severity: 'success'
        });
      }
      fetchEntityTypes();
      handleCloseEditDialog();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Unknown error occurred';
      setSnackbar({
        open: true,
        message: `Failed to ${editMode === 'edit' ? 'update' : 'create'} entity type: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const selectedMetricLevelNames = currentEntityType.metric_level_names || [];

  const moveMetricLevel = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= selectedMetricLevelNames.length) {
      return;
    }
    const next = [...selectedMetricLevelNames];
    const [item] = next.splice(index, 1);
    next.splice(targetIndex, 0, item);
    setCurrentEntityType(prev => ({ ...prev, metric_level_names: next }));
  };

  const removeMetricLevel = (index: number) => {
    const next = selectedMetricLevelNames.filter((_, i) => i !== index);
    setCurrentEntityType(prev => ({ ...prev, metric_level_names: next }));
  };

  const isFormValid = () => {
    return (
      currentEntityType.name.trim() !== '' &&
      currentEntityType.display_label.trim() !== '' &&
      currentEntityType.category.trim() !== '' &&
      currentEntityType.description.trim() !== '' &&
      selectedMetricLevelNames.length <= MAX_METRIC_LEVELS
    );
  };

  return (
    <Box>
      <TitleBar />
      <Container sx={{ mt: 4, mb: 6 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => navigate('/organization-setup')}
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  p: 1,
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Entity Type Setup</Typography>
            </Box>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
            >
              Add New Entity Type
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : entityTypes.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              No entity types found. Click the button above to add one.
            </Typography>
          ) : (
            <TableContainer sx={{ width: '100%' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', fontSize: '0.85rem' }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="12%" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell width="12%" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Display Label</TableCell>
                    <TableCell width="10%" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell width="18%" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell width="20%" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Review Dimensions</TableCell>
                    <TableCell width="18%" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Metric Levels</TableCell>
                    <TableCell width="10%" align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entityTypes.map((entityType, index) => {
                    const isLastInCategory = 
                      index === entityTypes.length - 1 || 
                      entityType.category !== entityTypes[index + 1].category;

                    return (
                      <TableRow 
                        key={entityType.name}
                        sx={isLastInCategory ? { '& > td': { borderBottom: '2px solid #bdbdbd' } } : {}}
                      >
                        <TableCell sx={{ fontSize: '0.85rem' }}>{entityType.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{entityType.display_label}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{entityType.category}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {entityType.description 
                              ? entityType.description.split(' ').slice(0, 6).join(' ') + 
                                (entityType.description.split(' ').length > 6 ? '...' : '')
                              : ''}
                            {entityType.description && entityType.description.split(' ').length > 6 && (
                              <InfoIcon fontSize="inherit" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.8rem' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {entityType.associated_review_dimension_names?.slice(0, 3).map((dimension, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}
                              >
                                {dimension}
                              </Box>
                            ))}
                            {entityType.associated_review_dimension_names && entityType.associated_review_dimension_names.length > 3 && (
                              <Box
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  color: '#666',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.75rem'
                                }}
                              >
                                +{entityType.associated_review_dimension_names.length - 3} more
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(entityType.applicable_metric_level_details || []).length > 0 ? (
                              entityType.applicable_metric_level_details?.map((level) => (
                                <Box
                                  key={level.id}
                                  sx={{
                                    backgroundColor: '#f3e5f5',
                                    color: '#7b1fa2',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 500
                                  }}
                                >
                                  L{level.level_number}: {level.display_label || level.name}
                                </Box>
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">None</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ padding: '4px' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                            <IconButton 
                              color="info" 
                              onClick={() => handleOpenViewDialog(entityType)}
                              size="small"
                              title="View Details"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color="primary" 
                              onClick={() => handleOpenEditDialog(entityType)}
                              size="small"
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              onClick={() => handleOpenDeleteDialog(entityType.name)}
                              size="small"
                              title="Delete"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the entity type "{deleteTarget}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>Entity Type Details</DialogTitle>
        <DialogContent>
          {viewEntityType && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Name
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {viewEntityType.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Display Label
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {viewEntityType.display_label}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Category
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {viewEntityType.category}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {viewEntityType.description}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Associated Review Dimensions
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {viewEntityType.associated_review_dimension_names && viewEntityType.associated_review_dimension_names.length > 0 ? (
                      viewEntityType.associated_review_dimension_names.map((dimension, index) => (
                        <Box
                          key={index}
                          sx={{
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}
                        >
                          {dimension}
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No review dimensions associated
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Applicable Metric Levels (ordered)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {viewEntityType.applicable_metric_level_details && viewEntityType.applicable_metric_level_details.length > 0 ? (
                      viewEntityType.applicable_metric_level_details.map((level) => (
                        <Box
                          key={level.id}
                          sx={{
                            backgroundColor: '#f3e5f5',
                            color: '#7b1fa2',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}
                        >
                          L{level.level_number}: {level.display_label || level.name}
                          {level.is_period_based ? ' (period)' : ''}
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No metric levels (only implicit review-entity / PROJECT grain)
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Add Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode === 'add' ? 'Add New Entity Type' : 'Edit Entity Type'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="name"
                label="Name"
                value={currentEntityType.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="display_label"
                label="Display Label"
                value={currentEntityType.display_label}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={categories}
                value={currentEntityType.category}
                onInputChange={(_, newValue) => {
                  const upperCaseValue = newValue?.toUpperCase() || '';
                  setCurrentEntityType(prev => ({
                    ...prev,
                    category: upperCaseValue
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Category"
                    required
                    helperText="Select existing or type new category (will be converted to uppercase)"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option}
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label="Description"
                value={currentEntityType.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={reviewDimensions}
                getOptionLabel={(option) => option.name}
                value={reviewDimensions.filter(dim => 
                  currentEntityType.review_dimension_names?.includes(dim.name)
                )}
                onChange={(_, newValue) => {
                  setCurrentEntityType(prev => ({
                    ...prev,
                    review_dimension_names: newValue.map(dim => dim.name)
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associated Review Dimensions"
                    placeholder="Select review dimensions"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Box
                        key={key}
                        {...tagProps}
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          margin: '2px'
                        }}
                      >
                        {option.name}
                      </Box>
                    );
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={metricLevels}
                getOptionLabel={(option) => `${option.display_label} (${option.name})`}
                value={selectedMetricLevelNames
                  .map(name => metricLevels.find(level => level.name === name))
                  .filter((level): level is MetricLevel => !!level)}
                onChange={(_, newValue) => {
                  // Preserve prior order for existing selections; append newly added at end.
                  const previous = selectedMetricLevelNames;
                  const nextNames = newValue.map(level => level.name);
                  const retained = previous.filter(name => nextNames.includes(name));
                  const added = nextNames.filter(name => !previous.includes(name));
                  const ordered = [...retained, ...added].slice(0, MAX_METRIC_LEVELS);
                  setCurrentEntityType(prev => ({
                    ...prev,
                    metric_level_names: ordered
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Applicable Metric Levels"
                    placeholder="Select metric levels (max 6)"
                    helperText="Order defines level numbers (L1 = lowest). Use arrows below to reorder. Top PROJECT grain is always implicit."
                  />
                )}
              />
              {selectedMetricLevelNames.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedMetricLevelNames.map((name, index) => {
                    const level = metricLevels.find(item => item.name === name);
                    return (
                      <Box
                        key={name}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          px: 1.5,
                          py: 0.5
                        }}
                      >
                        <Typography variant="body2">
                          L{index + 1}: {level?.display_label || name}
                          {level?.is_period_based ? ' (period-based)' : ''}
                        </Typography>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => moveMetricLevel(index, -1)}
                            disabled={index === 0}
                            title="Move up"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveMetricLevel(index, 1)}
                            disabled={index === selectedMetricLevelNames.length - 1}
                            title="Move down"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeMetricLevel(index)}
                            title="Remove"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitEntityType} 
            variant="contained" 
            disabled={!isFormValid()}
          >
            {editMode === 'add' ? 'Add' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EntityTypeSetup;
